<app-navigation></app-navigation>

<div class="deletion-container">
  <div class="deletion-hero">
    <h1>Eliminación de Datos del Usuario</h1>
    <p class="last-updated">Última actualización: {{ lastUpdated }}</p>
  </div>

  <div class="deletion-content">
    <section class="deletion-section highlight-section">
      <p class="intro-text">
        En Pezeta Solutions respetamos su derecho a la privacidad y al control sobre sus datos personales. Esta página describe de forma clara y directa cómo puede solicitar la eliminación de sus datos almacenados en la plataforma Chatbot Pezeta.
      </p>
    </section>

    <section class="deletion-section">
      <h2>1. Su Derecho a la Eliminación de Datos</h2>
      <p>
        De conformidad con la <strong>Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</strong> de México y, en los casos aplicables, con el <strong>Reglamento General de Protección de Datos (GDPR)</strong> de la Unión Europea, usted tiene derecho a solicitar la cancelación o supresión de sus datos personales cuando:
      </p>
      <ul>
        <li>Los datos ya no sean necesarios para los fines para los que fueron recopilados</li>
        <li>Retire su consentimiento y no exista otra base legal para el tratamiento</li>
        <li>Se oponga al tratamiento y no prevalezcan otros intereses legítimos</li>
        <li>Los datos hayan sido tratados de forma ilícita</li>
        <li>Exista una obligación legal de suprimir los datos</li>
      </ul>
      <p>
        Atendemos todas las solicitudes de eliminación de manera oportuna y con total transparencia sobre qué datos podemos eliminar y cuáles debemos retener por obligaciones legales.
      </p>
    </section>

    <section class="deletion-section">
      <h2>2. Datos que Podemos Eliminar</h2>
      <p>Podemos eliminar los siguientes tipos de datos personales a su solicitud:</p>

      <h3>2.1 Datos de Contactos y Leads</h3>
      <ul>
        <li>Nombre completo, número de teléfono y correo electrónico registrados en el CRM de la plataforma</li>
        <li>Historial de actividad como lead u oportunidad de negocio</li>
        <li>Notas y comentarios internos relacionados con su perfil</li>
        <li>Etiquetas, categorías y campos personalizados asociados a su registro</li>
      </ul>

      <h3>2.2 Historial de Conversaciones</h3>
      <ul>
        <li>Todos los mensajes intercambiados a través de WhatsApp Business mediante la plataforma</li>
        <li>Resúmenes y análisis de conversaciones generados por la inteligencia artificial</li>
        <li>Metadatos de conversaciones (fechas, duración, estado)</li>
      </ul>

      <h3>2.3 Archivos Multimedia</h3>
      <ul>
        <li>Imágenes, documentos, audios y videos enviados o recibidos durante conversaciones</li>
        <li>Archivos adjuntos almacenados en relación con su perfil o conversaciones</li>
      </ul>

      <h3>2.4 Registros de Actividad y Consentimientos</h3>
      <ul>
        <li>Registros de consentimiento de marketing (opt-in) y sus revocaciones</li>
        <li>Historial de campañas de mensajería recibidas</li>
        <li>Registros de interacción con el chatbot</li>
      </ul>
    </section>

    <section class="deletion-section">
      <h2>3. Cómo Solicitar la Eliminación</h2>
      <p>
        La eliminación de datos se solicita exclusivamente por <strong>correo electrónico</strong>. Dependiendo de si usted es un usuario final que interactuó con un chatbot o un cliente empresarial de la plataforma, el contenido de la solicitud varía:
      </p>

      <div class="option-card">
        <h3>Opción 1: Usuarios Finales (Personas que interactuaron con un chatbot)</h3>
        <p>
          Si usted es una persona que se comunicó con una empresa a través de WhatsApp y dicha empresa usa Chatbot Pezeta, solicite la eliminación de sus datos por correo electrónico:
        </p>

        <div class="step-list">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              <strong>Envíe un correo electrónico</strong>
              <p>Envíe un email a <a href="mailto:contacto@pezetasolutions.com">contacto&#64;pezetasolutions.com</a> con el asunto: <strong>"Solicitud de Eliminación de Datos"</strong>.</p>
              <p>Incluya en el cuerpo del mensaje:</p>
              <ul>
                <li>Su nombre completo</li>
                <li>El número de teléfono de WhatsApp con el que interactuó (con código de país, ej. +52 668 XXX XXXX)</li>
                <li>Nombre de la empresa con la que se comunicó (si lo recuerda)</li>
                <li>Descripción de los datos específicos que desea eliminar (o "todos mis datos")</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="option-card">
        <h3>Opción 2: Clientes Empresariales (Tenants de la plataforma)</h3>
        <p>
          Si usted es un cliente empresarial suscrito a la plataforma Chatbot Pezeta y desea solicitar la eliminación de su cuenta y todos los datos asociados:
        </p>

        <div class="step-list">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              <strong>Envíe un correo electrónico</strong>
              <p>Envíe un email a <a href="mailto:contacto@pezetasolutions.com">contacto&#64;pezetasolutions.com</a> con el asunto: <strong>"Solicitud de Baja y Eliminación de Datos - [Nombre de su empresa]"</strong> desde el correo electrónico registrado en su cuenta.</p>
              <p>Incluya en el cuerpo del mensaje:</p>
              <ul>
                <li>Razón social y nombre comercial de la empresa</li>
                <li>Identificador del tenant o slug de su cuenta</li>
                <li>Nombre de la persona autorizada que realiza la solicitud</li>
                <li>Confirmación expresa de que desea eliminar todos los datos asociados a la cuenta</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="deletion-section">
      <h2>4. Información a Incluir en su Solicitud</h2>
      <p>Para procesar su solicitud de manera eficiente, incluya la siguiente información:</p>
      <ul>
        <li><strong>Nombre completo</strong> del titular de los datos</li>
        <li><strong>Número de teléfono</strong> (con código de país) asociado al perfil en WhatsApp</li>
        <li><strong>Correo electrónico</strong> registrado, si aplica</li>
        <li><strong>Descripción de los datos a eliminar</strong>: puede especificar datos concretos o solicitar la eliminación total de todos los datos asociados a su identidad</li>
        <li><strong>Documentación de identidad</strong>: en casos donde no podamos verificar su identidad por otro medio, podremos solicitar una identificación oficial para proteger sus datos de solicitudes fraudulentas</li>
      </ul>
    </section>

    <section class="deletion-section">
      <h2>5. Plazo de Respuesta</h2>
      <div class="timeline">
        <div class="timeline-item">
          <div class="timeline-marker">48h</div>
          <div class="timeline-content">
            <strong>Confirmación de recepción</strong>
            <p>Confirmaremos la recepción de su solicitud dentro de las <strong>48 horas hábiles</strong> siguientes, con un número de folio de seguimiento.</p>
          </div>
        </div>
        <div class="timeline-item">
          <div class="timeline-marker">5d</div>
          <div class="timeline-content">
            <strong>Verificación de identidad</strong>
            <p>Si es necesario verificar su identidad, le solicitaremos la información necesaria dentro de los primeros <strong>5 días hábiles</strong>. El plazo de respuesta se suspende hasta recibir la verificación.</p>
          </div>
        </div>
        <div class="timeline-item">
          <div class="timeline-marker">30d</div>
          <div class="timeline-content">
            <strong>Eliminación completada</strong>
            <p>Completaremos la eliminación de sus datos dentro de un plazo máximo de <strong>30 días naturales</strong> a partir de la confirmación de su identidad y la viabilidad de la solicitud.</p>
          </div>
        </div>
        <div class="timeline-item">
          <div class="timeline-marker">OK</div>
          <div class="timeline-content">
            <strong>Confirmación de eliminación</strong>
            <p>Recibirá una confirmación por email o por el canal solicitado una vez que la eliminación se haya completado, indicando qué datos fueron eliminados y, en su caso, cuáles se retienen y por qué.</p>
          </div>
        </div>
      </div>
    </section>

    <section class="deletion-section">
      <h2>6. Excepciones: Datos que Podemos Estar Obligados a Retener</h2>
      <p>
        En algunos casos, la ley nos obliga a retener ciertos datos independientemente de su solicitud. Esto incluye:
      </p>
      <ul>
        <li>
          <strong>Obligaciones fiscales y contables:</strong> Registros de transacciones, facturas y datos relacionados con pagos que debemos conservar conforme al Código Fiscal de la Federación y la Ley del ISR (mínimo 5 años).
        </li>
        <li>
          <strong>Cumplimiento de mandatos legales:</strong> Datos que sean objeto de una orden judicial, requerimiento de autoridad competente o necesarios para la defensa de derechos legales en litigios vigentes o previsibles.
        </li>
        <li>
          <strong>Seguridad y prevención de fraude:</strong> Registros mínimos necesarios para prevenir el uso indebido de la plataforma o el fraude (por ejemplo, listas de bloqueo por abuso).
        </li>
        <li>
          <strong>Datos anonimizados:</strong> Datos que hayan sido irreversiblemente anonimizados y que ya no permitan identificar a ninguna persona no son datos personales y podrán ser retenidos para fines estadísticos.
        </li>
      </ul>
      <p>
        En todos los casos en que aplique una excepción, se lo informaremos por escrito, indicando la base legal específica de la retención y el plazo estimado de conservación.
      </p>
    </section>

    <section class="deletion-section">
      <h2>7. Confirmación de Eliminación</h2>
      <p>
        Una vez completado el proceso de eliminación, recibirá una notificación de confirmación al correo electrónico desde el que realizó la solicitud. La notificación incluirá:
      </p>
      <ul>
        <li>Fecha y hora de la eliminación</li>
        <li>Tipos de datos eliminados</li>
        <li>Relación de datos que, por obligación legal, debieron ser retenidos (con la base legal correspondiente)</li>
        <li>Número de folio de la solicitud para su archivo</li>
      </ul>
    </section>

    <section class="deletion-section">
      <h2>8. Datos Almacenados en los Servidores de Meta</h2>
      <div class="meta-notice">
        <p>
          <strong>Importante:</strong> Pezeta Solutions almacena y procesa los mensajes que recibe a través de los webhooks de Meta (WhatsApp Business API). Sin embargo, los mensajes que permanecen en la aplicación de WhatsApp en su dispositivo, en el historial de conversaciones de WhatsApp, o en los servidores de Meta Platforms, <strong>no están bajo el control de Pezeta Solutions</strong> y deben gestionarse directamente con Meta.
        </p>
        <p>Para gestionar o eliminar sus datos directamente con Meta, puede utilizar los siguientes recursos:</p>
        <ul>
          <li>
            <strong>Solicitud de eliminación de datos de Meta:</strong>
            <a href="https://www.facebook.com/help/contact/507739850846588" target="_blank" rel="noopener noreferrer">
              Herramienta de eliminación de datos de Meta
            </a>
          </li>
          <li>
            <strong>Configuración de privacidad de WhatsApp:</strong> Disponible directamente en la aplicación de WhatsApp en su dispositivo (Configuración &gt; Privacidad &gt; Solicitar información de la cuenta o Eliminar mi cuenta).
          </li>
          <li>
            <strong>Centro de privacidad de Meta:</strong>
            <a href="https://privacycenter.instagram.com/" target="_blank" rel="noopener noreferrer">
              Centro de Privacidad de Meta
            </a>
          </li>
        </ul>
      </div>
    </section>

    <section class="deletion-section">
      <h2>9. Información de Contacto para Solicitudes de Privacidad</h2>
      <p>
        Para solicitudes de eliminación de datos, preguntas sobre el proceso o ejercicio de cualquier otro derecho ARCO (Acceso, Rectificación, Cancelación y Oposición), puede contactarnos:
      </p>
      <div class="contact-info">
        <p><strong>Responsable de Privacidad</strong></p>
        <p><strong>Pezeta Solutions</strong></p>
        <p>Los Mochis, Sinaloa, México</p>
        <p>Email: <a href="mailto:contacto@pezetasolutions.com">contacto&#64;pezetasolutions.com</a></p>
        <p>Teléfono: +52 668 856 6833</p>
      </div>
      <p>
        Para mayor información sobre cómo tratamos sus datos, consulte nuestra <a routerLink="/politicas-de-privacidad">Política de Privacidad</a> completa.
      </p>
    </section>

    <section class="deletion-section consent-section">
      <h2>Su privacidad es nuestra prioridad</h2>
      <p>
        Nos comprometemos a procesar todas las solicitudes de eliminación de datos con la máxima diligencia, transparencia y respeto por sus derechos. Si en algún momento no está satisfecho con nuestra respuesta, tiene derecho a presentar una queja ante el <strong>Instituto Nacional de Transparencia, Acceso a la Información y Protección de Datos Personales (INAI)</strong> en México, o ante la autoridad de protección de datos de su país de residencia.
      </p>
    </section>
  </div>
</div>

<app-footer></app-footer>
